'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/app/utils';
import { playMediaSafely } from '@/app/utils/client';
import type { HeroVideoSource } from '../_constants/media';

interface HeroVideoCarouselProps {
  /** 按顺序轮播的视频源列表，含封面图 */
  sources: HeroVideoSource[];
  className?: string;
  /** 交叉淡入淡出时长（毫秒），默认 700 */
  fadeDurationMs?: number;
  /** 是否静音，默认 true。由外部「声音开关」控制。 */
  muted?: boolean;
  /** 当前播放视频索引变化时回调，供外部（如气泡）联动。 */
  onActiveIndexChange?: (index: number) => void;
  /** 当前播放视频时长（毫秒）就绪/变化时回调，供外部动画同步时长。 */
  onActiveDurationChange?: (durationMs: number) => void;
}

/**
 * Hero 区域顺序轮播：每条视频播完后交叉淡入淡出切到下一条，到末尾循环回第一条。
 * 采用双缓冲（所有视频叠层渲染），通过 opacity 过渡实现渐隐渐显，避免单元素重载造成的黑屏硬切。
 */
export const HeroVideoCarousel: React.FC<HeroVideoCarouselProps> = ({
  sources,
  className,
  fadeDurationMs = 700,
  muted = true,
  onActiveIndexChange,
  onActiveDurationChange,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);

  // 切换时：从头播放当前条，暂停其余条；并上报当前条时长
  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) return;
      if (index === activeIndex) {
        video.currentTime = 0;
        playMediaSafely(video);
      } else {
        video.pause();
      }
    });
    const active = videoRefs.current[activeIndex];
    if (active && Number.isFinite(active.duration) && active.duration > 0) {
      onActiveDurationChange?.(active.duration * 1000);
    }
  }, [activeIndex, onActiveDurationChange]);

  // 通知外部当前索引变化
  useEffect(() => {
    onActiveIndexChange?.(activeIndex);
  }, [activeIndex, onActiveIndexChange]);

  // 同步静音状态到所有 video 元素（用 DOM 属性比 JSX muted 更可靠）
  useEffect(() => {
    videoRefs.current.forEach((video) => {
      if (video) video.muted = muted;
    });
  }, [muted]);

  const handleEnded = () => {
    setActiveIndex((prev) => (prev + 1) % sources.length);
  };

  if (sources.length === 0) return null;

  return (
    <div className={cn('relative h-full w-full overflow-hidden bg-black', className)}>
      {sources.map(({ src, poster }, index) => (
        <video
          key={src}
          ref={(el) => {
            videoRefs.current[index] = el;
          }}
          // 5 条 video 都要在 parse 期带 autoplay：MIUI / vivo U4/T7 / TBS X5 会根据
          // parse 期属性把 <video> 分档——有 autoplay 的走"浏览器自动 inline 播放"档，
          // 之后 JS .play()/.pause() 都在 WebView 内；没有 autoplay 的走"用户主动播放"
          // 档，只允许用户手势触发的 .play() 走 inline，从 onEnded 里调 .play() 会被
          // 判成"用户要看这个视频"→ 拉起原生播放器浮层。轮播 2+ 都走 onEnded→.play()，
          // 必须提前归入自动 inline 档。
          // 资源开销：下方 [activeIndex] useEffect 在初次 mount（activeIndex=0）时对
          // 非 active 的 4 条立即 .pause()——早于任何解码：preload="auto" 到
          // canplaythrough 需要一次网络往返（几十到几百 ms），pause 早在网络数据到达
          // 前就把 paused=true，autoplay 触发条件失败，4 条不会进入解码状态。
          autoPlay
          muted
          playsInline
          // 老取值 x5-video-player-type="h5" 在 TBS 9+ / 新版 MIUI 已按普通视频劫持，
          // 必须用 "h5-page" 才走同层。国产内核在 HTML parse 期就锁定分档，因此这些
          // inline 属性必须写在 JSX 里让 React 首屏 render 就写进 HTML，不能靠 ref
          // 里 setAttribute 兜底（那时 parse 已结束）。
          webkit-playsinline="true"
          x5-playsinline="true"
          x5-video-player-type="h5-page"
          x5-video-player-fullscreen="false"
          x5-video-orientation="portrait"
          // T7/U4（vivo 自带浏览器、UC 系）不认 x5-*，独立走 t7-video-player-type。
          // X5/MIUI 会忽略这个属性，加上不冲突。
          t7-video-player-type="inline"
          preload="auto"
          controls={false}
          // 禁掉国产内核在视频上强制叠的原生控件：画中画、投屏、下载、全屏按钮。
          // Chromium 系（含 MIUI Browser）认这三个标准属性；倍速等国产内核独有的
          // 浮层按钮属于 WebView 之外的原生 UI，标准 API 无法关闭。
          disablePictureInPicture
          disableRemotePlayback
          controlsList="nodownload nofullscreen noremoteplayback noplaybackrate"
          poster={poster}
          className={cn(
            'absolute inset-0 h-full w-full object-cover object-center transition-opacity ease-in-out',
            index === activeIndex ? 'opacity-100' : 'opacity-0',
          )}
          style={{ transitionDuration: `${fadeDurationMs}ms` }}
          onEnded={index === activeIndex ? handleEnded : undefined}
          onLoadedMetadata={(e) => {
            if (index === activeIndex) {
              const d = e.currentTarget.duration;
              if (Number.isFinite(d) && d > 0) onActiveDurationChange?.(d * 1000);
            }
          }}
        >
          <source src={src} type="video/mp4" />
        </video>
      ))}
    </div>
  );
};
